/**
 * Public static paths for Smart Assistant fallback avatars (under apps/web/public).
 * Keep SQL fallbacks in assistantMessageSenderDisplaySql in sync with these paths.
 */
export const PUBLIC_SMART_ASSISTANT_ALIX_PATH = "/smartassistant/Alix-avatar.png" as const;
export const PUBLIC_SMART_ASSISTANT_MARVIN_PATH = "/smartassistant/Marvin-avatar.png" as const;

/** Default assistant name when DB has no personality row (matches SQL COALESCE tail). */
export const DEFAULT_ASSISTANT_NAME_FOR_DISPLAY = "Marvin" as const;

/** Site-relative path used as final COALESCE branch for system-message image in SQL. */
export const SQL_ASSISTANT_IMAGE_FALLBACK_PATH = PUBLIC_SMART_ASSISTANT_MARVIN_PATH;

/**
 * Fallback avatar path from assistant display name (Alix vs Marvin).
 * Matches SmartAssistantAvatar behavior; use for UI and documentation parity with SQL.
 */
export function getDefaultPublicSmartAssistantAvatarPathForName(name: string): string {
  const normalized = (name ?? DEFAULT_ASSISTANT_NAME_FOR_DISPLAY).toLowerCase();
  if (normalized === "alix") return PUBLIC_SMART_ASSISTANT_ALIX_PATH;
  return PUBLIC_SMART_ASSISTANT_MARVIN_PATH;
}

/**
 * Email-safe asset URL: leave absolute http(s) URLs unchanged (UploadThing, CDN, user photos);
 * prefix site-relative paths with the app base URL.
 */
export function toAbsolutePublicAssetUrl(baseUrl: string, url: string): string {
  const trimmed = (url ?? "").trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower.startsWith("http://") || lower.startsWith("https://")) return trimmed;
  if (trimmed.startsWith("/")) {
    const base = baseUrl.replace(/\/$/, "");
    return `${base}${trimmed}`;
  }
  return trimmed;
}

/** 64px avatar variants kept small for email embedding (~3KB vs ~40KB originals). */
export const PUBLIC_SMART_ASSISTANT_ALIX_EMAIL_PATH = "/smartassistant/Alix-avatar-64.png" as const;
export const PUBLIC_SMART_ASSISTANT_MARVIN_EMAIL_PATH = "/smartassistant/Marvin-avatar-64.png" as const;

/** Query params Clerk's image API uses to serve a small crop instead of the original upload. */
const CLERK_AVATAR_EMAIL_PARAMS = "width=64&height=64&fit=crop&quality=80";

/**
 * Avatar URL safe for email clients, sized for the 32px avatar slot:
 * - `data:` URIs (assistant personality SVG icons) are stripped by Gmail — substitute
 *   the small public PNG avatar matching the sender name.
 * - Clerk-hosted avatars are re-requested at 64px via Clerk's image API
 *   (~1.5KB instead of the 60-100KB original), so they load reliably through
 *   email image proxies.
 * - Site-relative paths are absolutized; other absolute URLs pass through.
 */
export function toEmailSafeAvatarUrl(baseUrl: string, url: string, senderName: string): string {
  const trimmed = (url ?? "").trim();
  if (trimmed.toLowerCase().startsWith("data:")) {
    const normalized = (senderName ?? "").trim().toLowerCase();
    const path =
      normalized === "alix"
        ? PUBLIC_SMART_ASSISTANT_ALIX_EMAIL_PATH
        : PUBLIC_SMART_ASSISTANT_MARVIN_EMAIL_PATH;
    return toAbsolutePublicAssetUrl(baseUrl, path);
  }
  try {
    const parsed = new URL(trimmed);
    if (parsed.hostname === "img.clerk.com" && !parsed.search) {
      return `${trimmed}?${CLERK_AVATAR_EMAIL_PARAMS}`;
    }
  } catch {
    // Not an absolute URL — fall through to path handling below.
  }
  return toAbsolutePublicAssetUrl(baseUrl, trimmed);
}
